import React from 'react';
import { styled } from '@mui/material/styles';
import { Paper } from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Passenger } from '../../../data/models/passenger.model';
import PassengerProfile from './PassengerProfile';
import PassengerReservations from './PassengerReservations';
import { ServicePlan } from '../../../data/models/service-plan.model';
import { Reservation } from '../../../data/models/reservation.model';
import PassengerProfileDialog from './PassengerProfileDialog';
import * as clientSlice from '../../../data/client.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { usePortalName } from '../../../hooks/usePortalName';
import { usePortalClientBC } from '../../../hooks/usePortalClientBC';

const PREFIX = 'PassengerDetails';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    gap: theme.spacing(3),
  },
}));

interface PassengerDetailsProps {
  passenger: Passenger | null;
  pastReservations: { reservations: Reservation[]; continuationToken?: string };
  futureReservations: {
    reservations: Reservation[];
    continuationToken?: string;
  };
  servicePlans: ServicePlan[];
  loadingPassenger: boolean;
  onLoadMorePastReservations?: () => void;
  onLoadMoreFutureReservations?: () => void;
}

export default function PassengerDetails({
  passenger,
  pastReservations,
  futureReservations,
  servicePlans,
  loadingPassenger,
  onLoadMorePastReservations,
  onLoadMoreFutureReservations,
}: PassengerDetailsProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const portalName = usePortalName();
  const portalClientBC = usePortalClientBC(portalName || '');

  const [editingPassenger, setEditingPassenger] = React.useState<Passenger>();
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);

  const clients = useAppSelector(clientSlice.selectClients);
  const genders = useAppSelector(lookupSlice.selectGenders);
  const createReasons = useAppSelector(
    lookupSlice.selectPassengerAuthorizationReasons,
  );

  React.useEffect(() => {
    dispatch(clientSlice.fetchClientsAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('passengerAuthorizationReason'));
    dispatch(lookupSlice.fetchLookupItemsAsync('gender'));
  }, []);

  const editPassenger = (passenger: Passenger) => {
    setEditingPassenger(passenger);
    setProfileDialogOpen(true);
  };

  const closePassengerProfileDialog = () => {
    setProfileDialogOpen(false);
  };

  return (
    <StyledBox elevation={0} className={classes.container}>
      <PassengerProfile
        passenger={passenger}
        servicePlans={servicePlans}
        edit={editPassenger}
        readonly={false}
      />
      <PassengerReservations
        passenger={passenger}
        loadingPassenger={loadingPassenger}
        servicePlans={servicePlans}
        pastReservations={pastReservations}
        futureReservations={futureReservations}
        onLoadMorePastReservations={onLoadMorePastReservations}
        onLoadMoreFutureReservations={onLoadMoreFutureReservations}
      />
      <PassengerProfileDialog
        title={t('passenger.editProfile')}
        portalClientBC={portalClientBC || undefined}
        passenger={editingPassenger}
        genders={genders}
        clients={clients}
        passengerAuthorizationReasons={createReasons}
        open={profileDialogOpen}
        close={closePassengerProfileDialog}
      />
    </StyledBox>
  );
}
